import {
  ColorInput,
  Fieldset,
  Flex,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconLink,
  IconX,
} from "@tabler/icons-react";
import type { BusinessInfoSettings } from "../../../types";

interface BusinessBrandingSectionProps {
  businessInfo: BusinessInfoSettings;
  setBusinessInfo: (businessInfo: BusinessInfoSettings) => void;
  children?: JSX.Element;
  isColorValid: boolean;
  validateColor: (color: string) => boolean;
  vanitySlugError: string | null;
  slugAvailability: { isAvailable: boolean; message: string } | null;
  validateVanitySlug: (slug: string) => boolean;
}

export const BusinessBrandingSection = ({
  businessInfo,
  setBusinessInfo,
  children,
  isColorValid,
  validateColor,
  vanitySlugError,
  slugAvailability,
  validateVanitySlug,
}: BusinessBrandingSectionProps) => {
  const handlePrimaryColorChange = (value: string) => {
    validateColor(value);
    setBusinessInfo({
      ...businessInfo,
      primaryColor: value,
    });
  };

  const handleLoginMessageChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    if (value.length <= 300) {
      setBusinessInfo({
        ...businessInfo,
        loginMessage: value,
      });
    }
  };

  const handleVanitySlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    if (validateVanitySlug(value)) {
      setBusinessInfo({
        ...businessInfo,
        vanitySlug: value,
      });
    }
  };

  return (
    <Fieldset
      radius="md"
      c="#0077cc"
      maw={600}
      legend={
        <Text size="sm" fw={600}>
          Business Branding
        </Text>
      }
    >
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {children}
        <div>
          <Textarea
            label="Business Description"
            description="Write a short intro about your business. This appears on your invite link."
            placeholder="Business description"
            radius="md"
            c="#000"
            maxRows={5}
            autosize
            minRows={3}
            value={businessInfo.loginMessage || ""}
            onChange={handleLoginMessageChange}
            error={
              businessInfo.loginMessage?.length === 200
                ? "Maximum 200 characters reached"
                : undefined
            }
          />
          <Text
            size="xs"
            c={
              (businessInfo.loginMessage?.length || 0) === 200 ? "red" : "gray"
            }
          >
            {businessInfo.loginMessage?.length || 0}/200
          </Text>
        </div>
        <Stack gap={8} align="flex-start">
          <ColorInput
            label="Primary Color"
            description="Choose your brand’s main color. It will be used for buttons, links, and highlights on your portal."
            format="hex"
            inputSize="xs"
            radius="md"
            c="#000"
            value={businessInfo.primaryColor || ""}
            onChange={handlePrimaryColorChange}
            data-cy="primaryColorInput"
            error={!isColorValid ? "Color is too light" : undefined}
          />
          {!isColorValid && (
            <Flex
              justify="center"
              align="flex-start"
              direction="row"
              wrap="nowrap"
              gap={6}
            >
              <IconAlertCircle color="red" size={20} />
              <Text size="xs" c="red" mb={5}>
                Oops! That color's a bit too light—try a darker one.
              </Text>
            </Flex>
          )}
        </Stack>
        <div className="vanity-slug-input">
          <TextInput
            description="This is your unique client portal link."
            leftSection={
              <Flex align="center" gap={2}>
                <IconLink size={13} /> app.smartwiz.io/
              </Flex>
            }
            leftSectionWidth={150}
            leftSectionProps={{
              style: {
                fontSize: "13px",
                backgroundColor: "#f0f0f0",
                color: "#404040",
                fontWeight: 500,
              },
            }}
            label="Portal URL"
            c="#000"
            radius="md"
            placeholder="enter your site name"
            value={businessInfo.vanitySlug || ""}
            onChange={handleVanitySlugChange}
            error={vanitySlugError}
            maxLength={15}
            data-cy="vanitySlugInput"
          />
          {slugAvailability && (
            <Flex
              justify="flex-start"
              align="center"
              direction="row"
              wrap="nowrap"
              gap={6}
              mt={4}
            >
              {slugAvailability.isAvailable ? (
                <IconCheck color="green" size={16} />
              ) : (
                <IconX color="red" size={16} />
              )}
              <Text
                size="xs"
                c={slugAvailability.isAvailable ? "green" : "red"}
              >
                {slugAvailability.message}
              </Text>
            </Flex>
          )}
        </div>
      </SimpleGrid>
    </Fieldset>
  );
};
